import { ShoppingCart, Eye, Heart, X } from 'lucide-react';
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from 'react';
import { useCurrency } from '../Currencycontext';
import './wishlist.css';

function Wishlist() {
  const { currency, convertPrice } = useCurrency();
  const navigate = useNavigate();

  const [config, setConfig] = useState(null);
  const [wishlist, setWishlist] = useState([]);
  const [userCartIds, setUserCartIds] = useState([]);
  const [ratings, setRatings] = useState({});
  const [pendingCartItem, setPendingCartItem] = useState(null);

  // Message state
  const [message, setMessage] = useState(null);

  // Load config.json
  useEffect(() => {
    fetch("/config.json")
      .then(res => res.json())
      .then(data => setConfig(data))
      .catch(err => console.error("Failed to load config.json:", err));
  }, []);

  // Fetch wishlist
  useEffect(() => {
    if (!config) return;
    fetch(`${config.BASE_URL}${config.GET_WISHLIST}`, { method: "POST", credentials: "include" })
      .then(res => res.json())
      .then(data => {
        if (data.success && data.wishlist) {
          setWishlist(data.wishlist);
        }
      });
  }, [config]);

  // Fetch cart
  useEffect(() => {
    if (!config) return;
    fetch(`${config.BASE_URL}${config.GET_CART}`, { method: "POST", credentials: "include" })
      .then(res => res.json())
      .then(data => {
        if (data.success && data.cart) {
          setUserCartIds(data.cart.map(item => item.product_id));
        }
      });
  }, [config]);

  // Fetch ratings
  useEffect(() => {
    if (!config || wishlist.length === 0) return;
    fetch(`${config.BASE_URL}${config.GET_PRODUCT_RATINGS}`, { method: "POST", credentials: "include" })
      .then(res => res.json())
      .then(data => {
        if (data.success && data.ratings) {
          const ratingsMap = {};
          for (let pid in data.ratings) {
            ratingsMap[pid] = parseFloat(data.ratings[pid].avg_rating || 0);
          }
          setRatings(ratingsMap);
        }
      });
  }, [config, wishlist]);

  // Add to cart handler
  useEffect(() => {
    if (!pendingCartItem || !config) return;

    const formData = new FormData();
    Object.entries(pendingCartItem).forEach(([k, v]) => formData.append(k, v));

    fetch(`${config.BASE_URL}${config.ADD_TO_CART}`, { method: "POST", body: formData, credentials: "include" })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setUserCartIds(prev => [...prev, pendingCartItem.product_id]);

          let count = Number(window.cartCount || localStorage.getItem("cartCount") || 0);
          count += 1;
          window.cartCount = count;
          localStorage.setItem("cartCount", count);
          window.dispatchEvent(new Event("cartUpdated"));

          // remove from wishlist after adding to cart
          setWishlist(prev => prev.filter(w => w.product_id !== pendingCartItem.product_id));

          // Show success message instead of alert
          setMessage({ type: "success", text: "Added to cart successfully!" });
        } else {
          setMessage({ type: "error", text: data.message || "Failed to add to cart" });
        }
      })
      .finally(() => setPendingCartItem(null));
  }, [pendingCartItem, config]);

  // Toggle wishlist
  const toggleWishlist = (product_id) => {
    if (!config) return;
    fetch(`${config.BASE_URL}${config.TOGGLE_WISHLIST}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ product_id })
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setWishlist(prev => prev.filter(item => item.product_id !== product_id));
          setMessage({ type: "info", text: "Removed from wishlist" });
        }
      });
  };

  //  Auto-hide message
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <div style={{ margin: 100 }} className="wishlist-page">
      {/*Glassy Message Box */}
      {message && (
        <div
          style={{
            position: "fixed",
            bottom: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            padding: "15px 20px",
            borderRadius: "12px",
            background: "rgba(255,255,255,0.15)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
            color: "black",
            fontSize: "14px",
            fontWeight: "500",
            zIndex: 1000,
            minWidth: "250px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <span>{message.text}</span>
          <X
            size={18}
            style={{ marginLeft: "15px", cursor: "pointer" }}
            onClick={() => setMessage(null)}
          />
        </div>
      )}

      <h2 
        className="wishlist-heading" 
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, fontWeight: 600,  marginTop: 60, marginBottom: 60 }}
      >
        My Wishlist
      </h2>

      {/* Wishlist grid */}
        <div className="wishlist-grid">
          {wishlist.length === 0 ? (
            <div
  style={{
    display: "flex",
    flexDirection: "column",
    justifyContent: "center", 
    alignItems: "center",     
    textAlign: "center",       
    marginTop: "50px",
    fontSize: "18px",
    color: "#666",
    padding: "0 20px"
  }}
>
  ❤️ Your wishlist is empty!
  <br />
  Tap the heart icon on any product to save it here — so you can quickly find and shop your favorites later.
  <br /><br />
  <button 
    onClick={() => navigate("/products")} 
    style={{ 
      padding: "10px 20px", 
      border: "none", 
      borderRadius: "5px", 
      background: "#ff6b6b", 
      color: "#fff", 
      fontSize: "16px", 
      cursor: "pointer" 
    }}
  >
    Continue Shopping
  </button>
</div>
          ) : (
            wishlist.map(product => {
              const avgRating = ratings[product.product_id] || 0;
              return (
                <div key={product.product_id} className="wishlist-card">
    <div className="wishlist-img-container">
      <img src={`/product_thumbnails/${product.product_id}/${product.image_path}`} alt={product.title} className="wishlist-img" />
      <div className="wishlist-badge" onClick={() => toggleWishlist(product.product_id)}>
        <Heart size={18} fill="red" color="red" />
      </div>
      <div className="wishlist-overlay">
        <div className="overlay-content">
          <button
            className="overlay-button add-to-cart"
            onClick={() => setPendingCartItem({ product_id: product.product_id, product_title: product.title, quantity: 1 })}
            disabled={userCartIds.includes(product.product_id)}
          >
            <ShoppingCart size={18} className="mr-1" /> {userCartIds.includes(product.product_id) ? "In Cart" : "Add to Cart"}
          </button>
          <button onClick={() => navigate("/product-details", { state: { product } })} className="overlay-button view-details">
            <Eye size={18} className="mr-1" /> View Details
          </button>
        </div>
      </div>
    </div>

  <div className="wishlist-content">
    <h3 className="wishlist-title">{product.title}</h3>
    <div className="wishlist-rating">
      <span className="stars">
        {Array.from({ length: 5 }, (_, i) => (
          <span key={i}>{i < Math.round(avgRating) ? "★" : "☆"}</span>
        ))}
      </span>
      <span className="rating-value">{avgRating.toFixed(1)}</span>
    </div>
    <p className="wishlist-price">{currency === "INR" ? "₹" : "$"} {convertPrice(product.price)}</p>
    <p className="wishlist-description">{product.description}</p>
  </div>
</div>

            );
          })
        )}
      </div>
    </div>
  );
}

export default Wishlist;
